import React, { useEffect, useRef } from 'react';
import { Activity } from 'lucide-react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

const HeatMap = ({ heatmapData }) => {
  const chartRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    // Dispose of previous chart if it exists
    if (rootRef.current) {
      rootRef.current.dispose();
    }

    // Create root element
    let root = am5.Root.new(chartRef.current);
    rootRef.current = root; // Store reference for cleanup

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Create chart
    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: root.verticalLayout
      })
    );

    // Process heatmap data from API or generate sample data
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    let data = [];
    let maxValue = 1;

    if (heatmapData && heatmapData.heatmap && Array.isArray(heatmapData.heatmap)) {
      // Use real data from API
      // API format: { hour: 0-23, day_index: 0-6 (Mon-Sun), count: number }
      const dayMap = { 0: 'Monday', 1: 'Tuesday', 2: 'Wednesday', 3: 'Thursday', 4: 'Friday', 5: 'Saturday', 6: 'Sunday' };
      
      // Create a map to store API data
      const dataMap = new Map();
      heatmapData.heatmap.forEach(item => {
        const key = `${item.day_index}-${item.hour}`;
        dataMap.set(key, item.count || 0);
        maxValue = Math.max(maxValue, item.count || 0);
      });
      
      // Generate all 168 data points (7 days × 24 hours)
      // Fill in missing data with 0
      for (let day = 0; day < 7; day++) {
        for (let hour = 0; hour < 24; hour++) {
          const key = `${day}-${hour}`;
          const value = dataMap.get(key) || 0;
          data.push({
            hour: hour,
            weekday: dayMap[day] || days[day],
            value: value
          });
        }
      }
      
      console.log('[HeatMap] Loaded', heatmapData.heatmap.length, 'data points, max value:', maxValue);
    } else {
      // Generate sample data if API data not available
      for (let day = 0; day < 7; day++) {
        for (let hour = 0; hour < 24; hour++) {
          // Simulate activity patterns
          let value = Math.random() * 30;

          // Weekdays (Mon-Fri)
          if (day < 5) {
            // Work hours (9-17)
            if (hour >= 9 && hour <= 17) {
              value = 60 + Math.random() * 40;
            }
            // Early morning/late evening
            else if (hour >= 6 && hour <= 22) {
              value = 30 + Math.random() * 30;
            }
          }
          // Weekends
          else {
            if (hour >= 10 && hour <= 20) {
              value = 20 + Math.random() * 40;
            }
          }

          const finalValue = Math.floor(value);
          data.push({
            hour: hour,
            weekday: days[day],
            value: finalValue
          });
          maxValue = Math.max(maxValue, finalValue);
        }
      }
    }

    // Create axes
    let yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'weekday',
        renderer: am5xy.AxisRendererY.new(root, {
          inversed: true,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
          minGridDistance: 20
        })
      })
    );

    yAxis.data.setAll(days.map(day => ({ weekday: day })));

    // Style Y axis labels
    yAxis.get('renderer').labels.template.setAll({
      fontSize: 12,
      fill: am5.color(0x00e5ff), // cyan
      fontWeight: '600'
    });

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'hour',
        renderer: am5xy.AxisRendererX.new(root, {
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
          minGridDistance: 20
        })
      })
    );

    xAxis.data.setAll(
      Array.from({ length: 24 }, (_, i) => ({ hour: i }))
    );

    // Style X axis labels
    xAxis.get('renderer').labels.template.setAll({
      fontSize: 11,
      fill: am5.color(0xb24bf3), // purple
      fontWeight: '500'
    });

    // Hide grid lines for cleaner look
    yAxis.get('renderer').grid.template.setAll({
      stroke: am5.color(0x1f1f1f),
      strokeOpacity: 0.2
    });

    xAxis.get('renderer').grid.template.setAll({
      stroke: am5.color(0x1f1f1f),
      strokeOpacity: 0.2
    });

    // Create series
    let series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        calculateAggregates: true,
        stroke: am5.color(0x0d0d0d),
        clustered: false,
        xAxis: xAxis,
        yAxis: yAxis,
        categoryXField: 'hour',
        categoryYField: 'weekday',
        valueField: 'value'
      })
    );

    series.columns.template.setAll({
      tooltipText: '{weekday}, {hour}:00\nActivity: {value}',
      strokeOpacity: 1,
      strokeWidth: 2,
      width: am5.percent(90),
      height: am5.percent(90)
    });

    // Calculate min and max values from the data for proper color distribution
    const values = data.map(d => d.value).filter(v => v > 0);
    const minValue = values.length > 0 ? Math.min(...values) : 0;
    const dataMaxValue = values.length > 0 ? Math.max(...values) : 1;
    
    console.log('[HeatMap] Color distribution - min:', minValue, 'max:', dataMaxValue, 'data points:', data.length);

    // Configure heat rules for neon colors
    series.set('heatRules', [
      {
        target: series.columns.template,
        min: am5.color(0x1f1f1f),
        max: am5.color(0x39ff14),
        dataField: 'value',
        key: 'fill',
        customFunction: function(sprite, min, max, value) {
          const actualValue = sprite.dataItem?.get('value') || 0;
          
          // If no data or value is 0, return dark color
          if (dataMaxValue === 0 || actualValue === 0) {
            sprite.set('fill', am5.color(0x1f1f1f));
            return;
          }
          
          // Normalize the value between 0 and 1 based on actual data range
          const range = dataMaxValue - minValue;
          const percent = range > 0 ? (actualValue - minValue) / range : 0;
          
          let color;
          if (percent < 0.2) {
            // Dark to Cyan (low activity)
            color = am5.Color.interpolate(percent / 0.2, am5.color(0x1f1f1f), am5.color(0x00e5ff));
          } else if (percent < 0.4) {
            // Cyan to Yellow
            color = am5.Color.interpolate((percent - 0.2) / 0.2, am5.color(0x00e5ff), am5.color(0xffff00));
          } else if (percent < 0.6) {
            // Yellow
            color = am5.color(0xffff00);
          } else if (percent < 0.8) {
            // Yellow to Orange
            color = am5.Color.interpolate((percent - 0.6) / 0.2, am5.color(0xffff00), am5.color(0xff6b35));
          } else {
            // Orange to Neon Green (high activity)
            color = am5.Color.interpolate((percent - 0.8) / 0.2, am5.color(0xff6b35), am5.color(0x39ff14));
          }
          
          sprite.set('fill', color);
        }
      }
    ]);

    series.data.setAll(data);

    // Customize tooltip
    let tooltip = am5.Tooltip.new(root, {
      getFillFromSprite: false,
      autoTextColor: false,
      pointerOrientation: 'horizontal'
    });

    tooltip.get('background').setAll({
      fill: am5.color(0x1a1a1a),
      fillOpacity: 0.95,
      stroke: am5.color(0x00e5ff),
      strokeWidth: 1
    });

    tooltip.label.setAll({
      fill: am5.color(0x00e5ff),
      fontSize: 12
    });

    series.columns.template.set('tooltip', tooltip);

    // Set background color
    chart.set('background', am5.Rectangle.new(root, {
      fill: am5.color(0x141414),
      fillOpacity: 0
    }));

    // Animate on load
    series.appear(1000);
    chart.appear(1000, 100);

    return () => {
      if (rootRef.current) {
        rootRef.current.dispose();
        rootRef.current = null;
      }
    };
  }, [heatmapData]);

  return (
    <div className="relative bg-dark-card border border-dark-border rounded-lg p-4 sm:p-6 overflow-hidden group"
         style={{
           background: '#141414',
           boxShadow: '0 0 2px rgba(178, 75, 243, 0.3)'
         }}>

      {/* Background Icon */}
      <div className="absolute -right-8 -top-8 opacity-[0.04] text-purple-accent pointer-events-none"
           style={{ filter: 'drop-shadow(0 0 25px currentColor) drop-shadow(0 0 50px currentColor)' }}>
        <Activity size={150} strokeWidth={1} />
      </div>

      {/* Header */}
      <div className="relative z-10 mb-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
          <div className="flex items-center space-x-3">
            <Activity className="text-purple-accent" size={24} strokeWidth={2.5}
                     style={{ filter: 'drop-shadow(0 0 10px currentColor) drop-shadow(0 0 20px currentColor)' }} />
            <h3 className="text-lg sm:text-xl font-bold text-purple-accent"
                style={{ textShadow: '0 0 20px currentColor, 0 0 40px currentColor' }}>
              Activity Heat Map
            </h3>
          </div>

          {/* Legend */}
          <div className="flex items-center space-x-2 text-xs">
            <span className="text-gray-500">Low</span>
            <div className="flex space-x-1">
              <div className="w-3 h-3 sm:w-4 sm:h-4 rounded"
                   style={{
                     background: '#00e5ff',
                     boxShadow: '0 0 8px rgba(0, 229, 255, 0.5)'
                   }} />
              <div className="w-3 h-3 sm:w-4 sm:h-4 rounded"
                   style={{
                     background: '#ffff00',
                     boxShadow: '0 0 10px rgba(255, 255, 0, 0.5)'
                   }} />
              <div className="w-3 h-3 sm:w-4 sm:h-4 rounded"
                   style={{
                     background: '#ff6b35',
                     boxShadow: '0 0 12px rgba(255, 107, 53, 0.5)'
                   }} />
              <div className="w-3 h-3 sm:w-4 sm:h-4 rounded"
                   style={{
                     background: '#39ff14',
                     boxShadow: '0 0 15px rgba(57, 255, 20, 0.6)'
                   }} />
            </div>
            <span className="text-gray-500">High</span>
          </div>
        </div>
        <p className="text-xs text-gray-500">Weekly activity patterns by hour of day</p>
      </div>

      {/* amCharts Heat Map */}
      <div
        ref={chartRef}
        className="relative z-10 w-full"
        style={{
          height: '400px',
          minHeight: '300px'
        }}
      />

      {/* Neon glow border effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none text-purple-accent rounded-lg"
           style={{ boxShadow: 'inset 0 0 20px currentColor, 0 0 20px currentColor' }} />
    </div>
  );
};

export default HeatMap;
